import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SectionHeader, Pill } from "@/components/Brand";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, KeyRound, Map } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [wards, setWards] = useState([]);
  const [tab, setTab] = useState("users");
  const [newSurveyor, setNewSurveyor] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    assigned_wards: [],
  });
  const [approveDlg, setApproveDlg] = useState(null);
  const [approvePw, setApprovePw] = useState("");
  const [assignDlg, setAssignDlg] = useState(null); // surveyor user
  const [assignSelected, setAssignSelected] = useState([]);

  const load = async () => {
    const [u, r, w] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/candidate-requests"),
      api.get("/admin/wards"),
    ]);
    setUsers(u.data);
    setRequests(r.data);
    setWards(w.data);
  };

  useEffect(() => {
    load();
  }, []);

  const createSurveyor = async (e) => {
    e.preventDefault();
    try {
      const trimmedEmail = (newSurveyor.email || "").trim().toLowerCase();
      const trimmedPass = (newSurveyor.password || "").trim();
      if (!trimmedPass) {
        toast.error("Password is required");
        return;
      }
      await api.post("/admin/surveyors", {
        ...newSurveyor,
        email: trimmedEmail,
        password: trimmedPass,
      });
      toast.success(
        `Surveyor created. Login: ${trimmedEmail} / ${trimmedPass} — share manually`,
        { duration: 12000 },
      );
      try {
        await navigator.clipboard.writeText(
          `Email: ${trimmedEmail}\nPassword: ${trimmedPass}`,
        );
        toast.message("Credentials copied to clipboard");
      } catch {
        /* clipboard may be blocked */
      }
      setNewSurveyor({ name: "", email: "", phone: "", password: "", assigned_wards: [] });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openAssign = (u) => {
    setAssignDlg(u);
    setAssignSelected(u.assigned_wards || []);
  };

  const saveAssignment = async () => {
    if (!assignDlg) return;
    try {
      await api.put(`/admin/surveyors/${assignDlg.id}/wards`, {
        assigned_wards: assignSelected,
      });
      toast.success(`Updated ${assignDlg.name}'s ward access`);
      setAssignDlg(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const approve = async () => {
    if (!approveDlg || !approvePw) return;
    try {
      await api.post("/admin/candidate-requests/approve", {
        request_id: approveDlg.id,
        password: approvePw,
      });
      toast.success(`Approved — login: ${approveDlg.email} / ${approvePw}`);
      setApproveDlg(null);
      setApprovePw("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/admin/candidate-requests/${id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <SectionHeader eyebrow="IAM" title="Users & Candidate Approvals" />

      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {[
          { k: "users", l: "All Users" },
          { k: "surveyors", l: "Create Surveyor" },
          { k: "requests", l: `Candidate Requests (${requests.filter((x) => x.status === "pending").length})` },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`users-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? "border-blue-700 text-blue-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="wi-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {["Name", "Email", "Role", "Phone", "Assigned Wards", "Created", ""].map((h) => (
                  <th key={h} className="px-4 py-3 data-label">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`user-row-${u.email}`}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <Pill tone={u.role === "admin" ? "blue" : u.role === "surveyor" ? "green" : "yellow"}>
                      {u.role}
                    </Pill>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.phone || "—"}</td>
                  <td className="px-4 py-3">
                    {u.role === "surveyor" ? (
                      <button
                        onClick={() => openAssign(u)}
                        data-testid={`assign-wards-${u.email}`}
                        className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100"
                      >
                        <Map size={12} />
                        {(u.assigned_wards || []).length} ward{(u.assigned_wards || []).length === 1 ? "" : "s"} · Manage
                      </button>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-500">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    {u.role !== "admin" && (
                      <button
                        onClick={() => removeUser(u.id)}
                        className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                        data-testid={`delete-user-${u.email}`}
                        aria-label="Delete user"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "surveyors" && (
        <form onSubmit={createSurveyor} className="wi-card p-6 max-w-2xl space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name *">
              <input required data-testid="new-surveyor-name" value={newSurveyor.name} onChange={(e) => setNewSurveyor({ ...newSurveyor, name: e.target.value })} className="wi-input" />
            </Field>
            <Field label="Email *">
              <input required type="email" data-testid="new-surveyor-email" value={newSurveyor.email} onChange={(e) => setNewSurveyor({ ...newSurveyor, email: e.target.value })} className="wi-input" />
            </Field>
            <Field label="Phone">
              <input data-testid="new-surveyor-phone" value={newSurveyor.phone} onChange={(e) => setNewSurveyor({ ...newSurveyor, phone: e.target.value })} className="wi-input" />
            </Field>
            <Field label="Password *">
              <input required type="text" data-testid="new-surveyor-password" value={newSurveyor.password} onChange={(e) => setNewSurveyor({ ...newSurveyor, password: e.target.value })} className="wi-input" />
            </Field>
          </div>
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-2">
              Assigned wards <span className="text-slate-400 font-normal">(surveyor can only submit in these)</span>
            </span>
            <div className="flex flex-wrap gap-2">
              {wards.map((w) => {
                const on = newSurveyor.assigned_wards.includes(w.id);
                return (
                  <button
                    key={w.id}
                    type="button"
                    data-testid={`new-surveyor-ward-${w.name.toLowerCase()}`}
                    onClick={() =>
                      setNewSurveyor({
                        ...newSurveyor,
                        assigned_wards: on
                          ? newSurveyor.assigned_wards.filter((x) => x !== w.id)
                          : [...newSurveyor.assigned_wards, w.id],
                      })
                    }
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      on
                        ? "bg-blue-700 border-blue-700 text-white"
                        : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"
                    }`}
                  >
                    {w.name}
                  </button>
                );
              })}
              {wards.length === 0 && <span className="text-xs text-slate-500">Create wards first.</span>}
            </div>
          </div>
          <button
            type="submit"
            data-testid="create-surveyor-btn"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-md font-medium text-sm"
          >
            <Plus size={16} /> Create surveyor account
          </button>
          <p className="text-xs text-slate-500">
            Share these credentials with the surveyor manually — no self-signup is allowed.
          </p>
        </form>
      )}

      {tab === "requests" && (
        <div className="space-y-3">
          {requests.length === 0 && <div className="text-sm text-slate-500">No requests yet.</div>}
          {requests.map((r) => (
            <div key={r.id} className="wi-card p-5" data-testid={`request-card-${r.email}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{r.email} · {r.phone}</div>
                  <div className="mt-1 text-sm text-slate-700">{r.party || "Independent"}</div>
                  {r.message && <div className="mt-2 text-sm text-slate-600 italic">"{r.message}"</div>}
                </div>
                <Pill tone={r.status === "approved" ? "green" : r.status === "rejected" ? "red" : "yellow"}>
                  {r.status}
                </Pill>
              </div>
              {r.status === "pending" && (
                <div className="mt-4 flex gap-2">
                  <button
                    data-testid={`approve-request-${r.email}`}
                    onClick={() => setApproveDlg(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md"
                  >
                    <Check size={14} /> Approve & create login
                  </button>
                  <button
                    data-testid={`reject-request-${r.email}`}
                    onClick={() => reject(r.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm rounded-md"
                  >
                    <X size={14} /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {approveDlg && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={18} className="text-blue-700" />
              <h3 className="text-lg font-semibold">Approve candidate</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Set a password for <span className="font-mono">{approveDlg.email}</span>. Share manually after creation.
            </p>
            <input
              autoFocus
              type="text"
              placeholder="Password"
              data-testid="approve-password-input"
              value={approvePw}
              onChange={(e) => setApprovePw(e.target.value)}
              className="wi-input mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setApproveDlg(null); setApprovePw(""); }} className="px-3 py-1.5 text-sm rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button
                onClick={approve}
                disabled={!approvePw}
                data-testid="approve-confirm-btn"
                className="px-4 py-1.5 text-sm rounded-md bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {assignDlg && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <Map size={18} className="text-blue-700" />
              <h3 className="text-lg font-semibold">Assign wards · {assignDlg.name}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Toggle wards this surveyor is allowed to submit surveys in. Removing all wards effectively suspends them.
            </p>
            <div className="flex flex-wrap gap-2 mb-5 max-h-72 overflow-y-auto">
              {wards.map((w) => {
                const on = assignSelected.includes(w.id);
                return (
                  <button
                    key={w.id}
                    type="button"
                    data-testid={`assign-toggle-${w.name.toLowerCase()}`}
                    onClick={() =>
                      setAssignSelected(
                        on
                          ? assignSelected.filter((x) => x !== w.id)
                          : [...assignSelected, w.id],
                      )
                    }
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      on
                        ? "bg-blue-700 border-blue-700 text-white"
                        : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"
                    }`}
                  >
                    {w.name} <span className="opacity-70">#{w.number}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setAssignSelected(wards.map((w) => w.id))}
                className="text-xs text-blue-700 hover:underline"
                data-testid="assign-select-all"
              >
                Select all
              </button>
              <div className="flex gap-2">
                <button onClick={() => setAssignDlg(null)} className="px-3 py-1.5 text-sm rounded-md border border-slate-200">Cancel</button>
                <button
                  onClick={saveAssignment}
                  data-testid="assign-save-btn"
                  className="px-4 py-1.5 text-sm rounded-md bg-blue-700 hover:bg-blue-800 text-white"
                >
                  Save ({assignSelected.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.wi-input{ width:100%; border:1px solid #cbd5e1; border-radius:6px; padding:0.55rem 0.75rem; font-size:0.875rem; }
        .wi-input:focus{ outline:2px solid #1d4ed8; outline-offset:2px; border-color:transparent; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
